import { useCallback, useMemo, useState } from 'react';
import { CalendarEventType, CalendarView, CALENDAR_EVENT_TYPES } from '../types/calendar';

function getViewStartDate(date: Date, view: CalendarView): Date {
  if (view === 'day') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  } else if (view === 'week') {
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    start.setHours(0, 0, 0, 0);
    return start;
  } else {
    // Month view: start from beginning of the week that contains the 1st
    const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());
    start.setHours(0, 0, 0, 0);
    return start;
  }
}

function getViewEndDate(date: Date, view: CalendarView): Date {
  if (view === 'day') {
    const end = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    end.setHours(23, 59, 59, 999);
    return end;
  } else if (view === 'week') {
    const end = new Date(date);
    end.setDate(date.getDate() - date.getDay() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  } else {
    // Month view: end at end of the week that contains the last day
    const lastOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const end = new Date(lastOfMonth);
    end.setDate(lastOfMonth.getDate() + (6 - lastOfMonth.getDay()));
    end.setHours(23, 59, 59, 999);
    return end;
  }
}

export function useCalendarFilters() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<CalendarEventType[]>([
    ...CALENDAR_EVENT_TYPES,
  ]);

  const toggleEventType = useCallback((type: CalendarEventType) => {
    setSelectedEventTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }, []);

  const navigateDate = useCallback(
    (direction: 'prev' | 'next' | 'today') => {
      if (direction === 'today') {
        setCurrentDate(new Date());
        return;
      }

      setCurrentDate(prev => {
        const next = new Date(prev);
        if (view === 'day') {
          next.setDate(next.getDate() + (direction === 'next' ? 1 : -1));
        } else if (view === 'week') {
          next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
        } else {
          next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
        }
        return next;
      });
    },
    [view]
  );

  const dateFrom = useMemo(
    () => getViewStartDate(currentDate, view).toISOString(),
    [currentDate, view]
  );

  const dateTo = useMemo(
    () => getViewEndDate(currentDate, view).toISOString(),
    [currentDate, view]
  );

  const filters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
      eventTypes:
        selectedEventTypes.length > 0 && selectedEventTypes.length < CALENDAR_EVENT_TYPES.length
          ? selectedEventTypes
          : undefined,
    }),
    [dateFrom, dateTo, selectedProjectIds, selectedEventTypes]
  );

  return {
    currentDate,
    view,
    selectedProjectIds,
    selectedEventTypes,
    filters,
    setCurrentDate,
    setView,
    setSelectedProjectIds,
    toggleEventType,
    navigateDate,
  };
}
